import React from 'react';
import { Icon } from '@iconify/react';
import Swal from 'sweetalert2';
import { useSimulation } from '../../contexts/SimulationContext';

const DevTools = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { project, simulateArrivals, moveCandidates, failCandidates, triggerFailure } = useSimulation();

  // Fail with SweetAlert confirmation + empty queue awareness
  const handleFail = async ({ label, atStage, atSubStage }) => {
    const result = await Swal.fire({
      title: 'Eliminate Candidate?',
      html: `<div style="font-size:14px;color:#6b7280;line-height:1.6">Permanently fail <b>1 candidate</b> at<br/><span style="color:#800000;font-weight:800">${label}</span><br/>This action cannot be undone.</div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: '✕ Yes, Eliminate',
      cancelButtonText: 'Keep Active',
      background: 'var(--white)',
      color: 'var(--text-dark)',
    });

    if (result.isConfirmed) {
      await failCandidates({ atStage, atSubStage, count: 1 });
    }
  };

  return (
    <div style={{ 
      position: 'fixed', 
      bottom: '24px', 
      right: '24px', 
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '12px'
    }}>
      {isOpen && (
        <div className="glass-card hide-scrollbar" style={{ 
          width: '340px', 
          maxHeight: '480px', 
          overflowY: 'auto',
          border: '2px solid var(--primary)',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', color: 'var(--primary)' }}>
            <Icon icon="solar:bug-bold-duotone" width="24" height="24" />
            <h3 style={{ fontSize: '14px', fontWeight: 800 }}>SIMULATION ENGINE</h3>
          </div>

          {!project ? (
            <div style={{ 
              padding: '24px 16px', 
              textAlign: 'center', 
              background: 'rgba(128,0,0,0.03)', 
              borderRadius: '12px',
              border: '1px dashed var(--primary)'
            }}>
              <Icon icon="solar:shield-warning-bold-duotone" width="48" height="48" color="var(--primary)" style={{ opacity: 0.5, marginBottom: '12px' }} />
              <div style={{ fontWeight: 800, fontSize: '13px', color: 'var(--primary)', marginBottom: '4px' }}>Project Required</div>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                Please select a project from the dashboard to enable simulation controls.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>

              {/* Arrivals — no fail button (no test to fail) */}
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Candidate Arrivals</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <SimButton onClick={() => simulateArrivals(10)} label="+10 Entry" color="var(--primary)" />
                  <SimButton onClick={() => simulateArrivals(50)} label="+50 Entry" color="var(--primary)" />
                </div>
              </div>

              {/* Pipeline Shifts — Entry→Batch and Batch→Run have NO fail button */}
              <div style={{ padding: '12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#0369a1', marginBottom: '8px', textTransform: 'uppercase' }}>Pipeline Sequential Shift</div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {/* No ✕ — Entry and Batch are pre-test stages */}
                  <SimButton onClick={() => moveCandidates({ from: 'entry', to: 'batch', count: 5 })} label="▶ Entry → Batch (5)" color="#0ea5e9" />
                  <SimButton onClick={() => moveCandidates({ from: 'batch', to: 'run', count: 5 })} label="▶ Batch → Run (5)" color="#8b5cf6" />
                  {/* Run → Physical has fail option (tested in run) */}
                  <MoveRow
                    label={`Run → ${project.stages?.[0]?.name || 'Stage 1'} (5)`}
                    onMove={() => moveCandidates({ from: 'run', to: 'stage', toSub: project.stages?.[0]?.name, count: 5 })}
                    onFail={() => handleFail({ label: 'Track Run', atStage: 'run' })}
                  />
                </div>
              </div>

              {/* Physical Sub-Stages — all have fail button */}
              {project.stages && project.stages.length > 0 && (
                <div style={{ padding: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#166534', marginBottom: '8px', textTransform: 'uppercase' }}>Physical Test Sub-Stages</div>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {project.stages.map((stage, idx) => {
                      const nextStage = project.stages[idx + 1];
                      return (
                        <MoveRow
                          key={stage.name}
                          label={`${stage.name} → ${nextStage ? nextStage.name : 'Medical'} (5)`}
                          onMove={() => {
                            if (nextStage) {
                              moveCandidates({ from: 'physical_test', fromSub: stage.name, to: 'physical_test', toSub: nextStage.name, count: 5 });
                            } else {
                              moveCandidates({ from: 'physical_test', fromSub: stage.name, to: 'medical', count: 5 });
                            }
                          }}
                          onFail={() => handleFail({ label: stage.name, atStage: 'physical_test', atSubStage: stage.name })}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Final Stage — has fail button */}
              <div style={{ padding: '12px', background: '#fdf9f0', border: '1px solid #fed7aa', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#92400e', marginBottom: '8px', textTransform: 'uppercase' }}>Final Stages</div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <MoveRow
                    label="Medical → Completed (5)"
                    onMove={() => moveCandidates({ from: 'medical', to: 'completed', count: 5 })}
                    onFail={() => handleFail({ label: 'Medical Check', atStage: 'medical' })}
                  />
                </div>
              </div>

              {/* System Anomalies */}
              <div style={{ padding: '12px', background: '#f1f5f9', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#475569', marginBottom: '8px', textTransform: 'uppercase' }}>System Anomalies</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <SimButton onClick={() => triggerFailure('Scanner B4')} label="Device Fail" color="#ef4444" />
                  <SimButton onClick={() => triggerFailure('Network Hub')} label="Ping Loss" color="#ef4444" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <button onClick={() => setIsOpen(!isOpen)} style={{ 
        width: '56px', 
        height: '56px', 
        background: 'var(--primary)', 
        color: 'white', 
        borderRadius: '16px', 
        border: 'none', 
        cursor: 'pointer',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon icon={isOpen ? "solar:close-circle-bold-duotone" : "solar:bug-bold-duotone"} width="32" height="32" />
      </button>
    </div>
  );
};

// Row with ▶ Move and ✕ Fail buttons (used only for test stages)
const MoveRow = ({ label, onMove, onFail }) => (
  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
    <button
      onClick={onMove}
      style={{
        flex: 1,
        padding: '7px 4px',
        background: 'white',
        border: '1px solid #0ea5e9',
        color: '#0ea5e9',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'left'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#0ea5e9'; e.currentTarget.style.color = 'white'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#0ea5e9'; }}
    >
      ▶ {label}
    </button>
    <button
      onClick={onFail}
      title="Eliminate 1 candidate at this stage"
      style={{
        width: '32px',
        height: '32px',
        background: '#fff1f2',
        border: '1px solid #fecaca',
        color: '#ef4444',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: 900,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = 'white'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff1f2'; e.currentTarget.style.color = '#ef4444'; }}
    >
      ✕
    </button>
  </div>
);

const SimButton = ({ onClick, label, color = '#64748b' }) => (
  <button 
    onClick={onClick}
    style={{ 
      flex: 1,
      padding: '8px 4px', 
      background: 'white', 
      border: `1px solid ${color}`, 
      color: color, 
      borderRadius: '6px', 
      fontSize: '11px', 
      fontWeight: 700, 
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => { e.target.style.background = color; e.target.style.color = 'white'; }}
    onMouseLeave={(e) => { e.target.style.background = 'white'; e.target.style.color = color; }}
  >
    {label}
  </button>
);

export default DevTools;
